/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2020, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import TranslatableAsyncObject from 'utils/TranslatableAsyncObject';
import { isEmpty, get, isNil, uniqBy } from 'lodash';
import { removeParserKeys } from 'utils/common';
import ApiCommon from './api-common';

const SCHEMA_FORMAT_MATCH = new RegExp('^(.*);version=([0-9.]+|draft-[0-9]+)');

const AVRO_SCHEMA = 'Avro';
const ASYNCAPI_SCHEMA = 'AsyncAPI';
const OPENAPI_SCHEMA = 'OpenAPI';
const JSON_SCHEMA = 'JSON';

const SUPPORTED_SCHEMA_FORMATS = {
	'application/vnd.aai.asyncapi': ASYNCAPI_SCHEMA,
	'application/vnd.aai.asyncapi+json': ASYNCAPI_SCHEMA,
	'application/vnd.aai.asyncapi+yaml': ASYNCAPI_SCHEMA,
	'application/vnd.oai.openapi': OPENAPI_SCHEMA,
	'application/vnd.oai.openapi+json': OPENAPI_SCHEMA,
	'application/vnd.oai.openapi+yaml': OPENAPI_SCHEMA,
	'application/schema+json': JSON_SCHEMA,
	'application/schema+yaml': JSON_SCHEMA,
	'application/vnd.apache.avro': AVRO_SCHEMA,
	'application/vnd.apache.avro+json': AVRO_SCHEMA,
	'application/vnd.apache.avro+yaml': AVRO_SCHEMA
};

const NON_PRIMITIVE_TYPES = ['object', 'record'];

export const getReadableSchemaFormat = (schemaFormat) => {
	const typeAndVersion = getSchemaTypeAndVersion(schemaFormat);
	//TODO should type be a function here?
	if (typeAndVersion && typeAndVersion.type) {
		typeAndVersion.type = SUPPORTED_SCHEMA_FORMATS[typeAndVersion.type];
	}
	return typeAndVersion;
};

export const getSchemaTypeAndVersion = (schemaFormat) => {
	let result = { type: null, version: null };
	if (schemaFormat) {
		const formatMatch = schemaFormat.match(SCHEMA_FORMAT_MATCH);
		if (formatMatch && formatMatch.length > 2) {
			result.type = formatMatch[1];
			result.version = formatMatch[2];
		}
	}
	return result;
};


export const hasMessages = (operation) => {
	return operation.messages().length > 0;
};

export default class AsyncApi extends ApiCommon {
	/**
	 * checks that the given file is a supported AsyncAPI version
	 */
	isSupported() {
		return (Boolean)(this.api?._json?.asyncapi);
	}

	/**
	 * @returns the endpoint urls and endpoint display labels as an array
	 */
	getEndpoints() {
		let endpoints = [];

		if (this.api.servers().length > 0) {
			const protocol = this.getFirstProtocol();
			endpoints = Object.values(this.api.servers().all())
				.filter((server) => { return server.protocol() === protocol })
				.map((server) => ({ url: server.url(), types: server.id(), variables: server.variables(), bindings: server.bindings() }));
		}
		return endpoints;
	}

	/**
	 * @returns all schemas in the api
	 */
	getSchemas() {
		return {};
	}

	isAsyncApiSchema(schemaType) {
		return schemaType === ASYNCAPI_SCHEMA;
	}

	isAvroSchema(schemaType) {
		return schemaType === AVRO_SCHEMA;
	}

	isJSONSchema(schemaType) {
		return schemaType === JSON_SCHEMA;
	}

	isOpenApiSchema(schemaType) {
		return schemaType === OPENAPI_SCHEMA;
	}


	getFirstMessageHeader(channelName, operationId) {
		const channel = this.api.channels().get(channelName);
		const operation = channel.operations().get(operationId)
		if (!channel || !operation || operation.messages().all().length === 0) return { schema: null, example: null };
		const firstMessage = operation.messages().all()[0];
		if (!firstMessage.headers()) return { schema: null, example: null };

		const schema = removeParserKeys(firstMessage.headers().json());
		const examples = firstMessage.examples();
		const example = examples?.[0]?.headers() || null;

		return {
			schema,
			example,
		};
	}
	/**
	 * @returns first schema from a operation
	 */

	getFirstSchema(channelName, operationId) {
		const channel = this.api.channels().get(channelName);
		const operation = channel?.operations()?.get(operationId)
		if (channel && operation && hasMessages(operation)) {
			const message = operation.messages().all()[0];
			let payload = message.payload();
			const format = message.schemaFormat();
			const examples = message.examples();
			const encodingType = message.contentType();
			let example = null;
			if (typeof message._json['x-parser-original-payload'] === 'string') {
				payload = { type: () => message._json['x-parser-original-payload'] };
			} else {
				payload = message.payload()
			}
			if (examples && examples.all().length > 0 && examples.all()[0].payload()) {
				example = examples.all()[0].payload();
			}

			const schemaTypeAndVersion = getReadableSchemaFormat(format);
			return { payload, format, example, type: schemaTypeAndVersion.type, version: schemaTypeAndVersion.version, encodingType };
		}
		else {
			return { payload: null, format: null, example: null, type: null, version: null, encodingType: null };
		}
	}
	/**
	 * @returns first message from a channel
	 */
	getFirstMessage(channelName, operationId) {
		const channel = this.api.channels().get(channelName);
		const operation = channel.operations().get(operationId)
		if (!operation || operation.messages().all().length === 0) return {}
		const translatableMessage = new TranslatableAsyncObject(operation.messages().all()[0]);
		const bindings = operation.messages().all()[0].bindings();
		return { description: translatableMessage.description(), summary: translatableMessage.summary(), title: translatableMessage.title(), bindings: bindings };
	}

	/**
	 * @returns all channels definined in the api
	 */
	getChannelNames() {
		let channels = [];
		if (this.api.channels().all().length > 0) {
			channels = this.api.channels().all().map((channel) => channel.id());
		}

		return channels;
	}

	/**
	 * @returns first protocol defined in the api servers
	 */
	getFirstProtocol() {
		let protocol = '';
		if (this.api.allServers().length > 0) {
			protocol = Object.values(this.api.servers())[0].protocol();
		}

		return protocol;
	}

	getFirstSecurityType() {
		if (this.api.servers().all().length > 0) {
			const securityList = Object.values(this.api.servers())[0].security();
			if (securityList && this.api.securitySchemes().all().length > 0) {
				const securityScheme = securityList[0].all()[0].scheme()
				return {
					type: securityScheme.type(),
					description: new TranslatableAsyncObject(securityScheme).description()
				};
			}
		}
		return {
			type: null,
			description: null
		};

	}

	getChannel(channelName) {
		return this.api.channels().get(channelName);
	}

	getChannelBindings(channelName) {
		const channel = this.getChannel(channelName);
		if (channel && channel.bindings().all().length > 0) {
			return channel.bindings();
		}
		return null;
	}

	getMessage(channelName, operation) {
		const channel = this.getChannel(channelName);
		if (!channel)
			return null;
		return channel.operations().get(operation)?.messages()?.all()[0] ?? null
	}

	getMessageBindings(message) {
		if (message && message.bindings().all().length > 0) {
			return message.bindings();
		}
		return null;
	}

	getDescription(channelName) {
		const channel = this.getChannel(channelName);
		if (channel && channel.hasDescription()) {
			return new TranslatableAsyncObject(channel).description();
		}
		return null;
	}

	getParameters(channelName) {
		const channel = this.getChannel(channelName);
		if (channel && channel._json && channel._json.parameters) {
			let parameters = [];
			for (let param in channel._json.parameters) {
				parameters.push({
					name: param,
					description: channel._json.parameters[param].description || null,
					schema: channel._json.parameters[param].schema || null,
					location: channel._json.parameters[param].location || null
				});
			}
			return parameters;
		}
		return [];
	}

	isPrimitiveSchema(payload) {
		if (isNil(payload) || (payload && !isEmpty(payload) && (typeof payload === 'string' || !NON_PRIMITIVE_TYPES.includes(payload.type())))) {
			return true;
		}
		return false;
	}

	getPrimitiveSchemaInfo(payload) {
		const primitivePayloadInformation = {};
		if (payload && typeof payload === 'string') {
			primitivePayloadInformation.type = payload;
		} else if (payload && payload.type()) {
			primitivePayloadInformation.type = payload.type();
			if (payload.format) primitivePayloadInformation.format = payload.format;
		}

		return primitivePayloadInformation;
	}

	getServerBindings(serverName) {
		const servers = this.api.servers();
		const server = servers[serverName];
		if (server && server.bindings().all().length > 0) {
			return server.bindings();
		}
		return null;
	}

	getSecurityDefinitions() {
		let result = {};
		if (this.api.securitySchemes()) {
			const schemes = this.api.securitySchemes().all()
			schemes.forEach((scheme) => {
				if (!Array.isArray(scheme) && Object.values(scheme).length > 0) {
					result[scheme._meta.id] = scheme.json();
				}
			});
		}
		console.debug(result)
		return result;
	}

	getSecurity() {
		const servers = this.api.servers();
		const allServerSecurity = [...servers].flatMap((server) => {
			if (server.security()) {
				if (server.security().length === 0) {
					return []
				}
				const securities = server.security().pop()
				const result = Object.fromEntries(
					securities.collections.map(security => {
						return [security.scheme()._meta.id, security.json().scopes]
					})
				);
				return result;
			} else {
				return [];
			}
		});
		return uniqBy(allServerSecurity, (securityRequirement) => Object.keys(securityRequirement)[0]);
	}

	isEnforced() {
		return get(this.api._json, 'x-ibm-configuration.enforced', false);
	}
}

export { AsyncApi };
